import { type AggregationFn } from "@tanstack/react-table";

import { type ColumnType } from "../validation/parseColumnType";
import { range as dateRangeDays } from "./dateRangeDays";
import { range as dateRangeMonths } from "./dateRangeMonths";
import { empty } from "./empty";
import { extent } from "./extent";
import { filled } from "./filled";
import { max } from "./max";
import { maxDate } from "./maxDate";
import { mean } from "./mean";
import { median } from "./median";
import { min } from "./min";
import { minDate } from "./minDate";
import { empty as percentEmpty } from "./percentEmpty";
import { filled as percentFilled } from "./percentFilled";
import { unique as percentUnique } from "./percentUnique";
import { range } from "./range";
import { stdev } from "./standardDeviation";
import { sum } from "./sum";
import { unique } from "./unique";

//
// Some implementation notes.
//
// Some functions in this file are blank-page rewrites of the MIT-licensed aggregation functions
// implemented in `@tanstack/react-table`[1] core. Our goal in the this reimplementation is to be:
//
//   1. More type-safe, e.g., preferring `unknown` over `any`.
//   2. Better tested. There are 0 tests of these functions in `@tanstack/react-table` core.
//   3. Better suited to our style guides. TanStack is written in the same Spartan, old-skool style
//      that ProseMirror and Mousetrap are written in, and for better and for worse, that style is
//      not what Moment has landed on.
//
// [1]: https://github.com/TanStack/table/blob/3402597db06a00db7528b5b6ae39bfee1b6bf86b/packages/table-core/src/aggregationFns.ts
//

export interface AggregationFnOption<T> {
	label: string;
	value?: AggregationFn<T>;
}

const sumOption = { label: "Sum", value: sum } as const;
const meanOption = { label: "Mean", value: mean } as const;
const medianOption = { label: "Median", value: median } as const;
const minOption = { label: "Min", value: min } as const;
const maxOption = { label: "Max", value: max } as const;
const rangeOption = { label: "Range", value: range } as const;
const extentOption = { label: "Extent", value: extent } as const;
const standardDeviationOption = { label: "Standard Deviation", value: stdev } as const;
const emptyOption = { label: "Empty", value: empty } as const;
const filledOption = { label: "Filled", value: filled } as const;
const uniqueOption = { label: "Unique", value: unique } as const;
const percentEmptyOption = { label: "Percent Empty", value: percentEmpty } as const;
const percentFilledOption = { label: "Percent Filled", value: percentFilled } as const;
const percentUniqueOption = { label: "Percent Unique", value: percentUnique } as const;
const earliestDateOption = { label: "Earliest Date", value: minDate } as const;
const latestDateOption = { label: "Latest Date", value: maxDate } as const;
const dateRangeDaysOption = { label: "Date Range (Days)", value: dateRangeDays } as const;
const dateRangeMonthsOption = { label: "Date Range (Weeks)", value: dateRangeMonths } as const;

export const noneOption: AggregationFnOption<any> = { label: "None", value: undefined };

export const builtinAggregationFns = [
	sumOption,
	meanOption,
	medianOption,
	minOption,
	maxOption,
	rangeOption,
	extentOption,
	standardDeviationOption,
	emptyOption,
	filledOption,
	uniqueOption,
	percentEmptyOption,
	percentFilledOption,
	percentUniqueOption,
	earliestDateOption,
	latestDateOption,
	dateRangeDaysOption,
	dateRangeMonthsOption,
] as const;

export type BuiltinAggregationFnLabels = (typeof builtinAggregationFns)[number]["label"];

export interface BuiltinAggregationFnOption<T> {
	label: (typeof builtinAggregationFns)[number]["label"];
	value?: AggregationFn<T>;
}

export const aggregationFns: Record<ColumnType, Map<string, AggregationFnOption<unknown>>> = {
	"Boolean": new Map<string, AggregationFnOption<unknown>>([
		[emptyOption.label, emptyOption],
		[filledOption.label, filledOption],
		[percentEmptyOption.label, percentEmptyOption],
		[percentFilledOption.label, percentFilledOption],
	]),
	"Number": new Map<string, AggregationFnOption<unknown>>([
		[sumOption.label, sumOption],
		[meanOption.label, meanOption],
		[medianOption.label, medianOption],
		[minOption.label, minOption],
		[maxOption.label, maxOption],
		[rangeOption.label, rangeOption],
		[extentOption.label, extentOption],
		[standardDeviationOption.label, standardDeviationOption],
		[emptyOption.label, emptyOption],
		[filledOption.label, filledOption],
		[uniqueOption.label, uniqueOption],
		[percentEmptyOption.label, percentEmptyOption],
		[percentFilledOption.label, percentFilledOption],
		[percentUniqueOption.label, percentUniqueOption],
	]),
	"URL": new Map<string, AggregationFnOption<unknown>>([
		[emptyOption.label, emptyOption],
		[filledOption.label, filledOption],
		[uniqueOption.label, uniqueOption],
		[percentEmptyOption.label, percentEmptyOption],
		[percentFilledOption.label, percentFilledOption],
		[percentUniqueOption.label, percentUniqueOption],
	]),
	"Date": new Map<string, AggregationFnOption<unknown>>([
		[emptyOption.label, emptyOption],
		[filledOption.label, filledOption],
		[uniqueOption.label, uniqueOption],
		[percentEmptyOption.label, percentEmptyOption],
		[percentFilledOption.label, percentFilledOption],
		[percentUniqueOption.label, percentUniqueOption],
		[earliestDateOption.label, earliestDateOption],
		[latestDateOption.label, latestDateOption],
		[dateRangeDaysOption.label, dateRangeDaysOption],
		[dateRangeMonthsOption.label, dateRangeMonthsOption],
	]),
	"Time": new Map<string, AggregationFnOption<unknown>>([
		[emptyOption.label, emptyOption],
		[filledOption.label, filledOption],
		[uniqueOption.label, uniqueOption],
		[percentEmptyOption.label, percentEmptyOption],
		[percentFilledOption.label, percentFilledOption],
		[percentUniqueOption.label, percentUniqueOption],
		[earliestDateOption.label, earliestDateOption],
		[latestDateOption.label, latestDateOption],
		[dateRangeDaysOption.label, dateRangeDaysOption],
		[dateRangeMonthsOption.label, dateRangeMonthsOption],
	]),
	"Time distance to now": new Map<string, AggregationFnOption<unknown>>([
		[emptyOption.label, emptyOption],
		[filledOption.label, filledOption],
		[uniqueOption.label, uniqueOption],
		[percentEmptyOption.label, percentEmptyOption],
		[percentFilledOption.label, percentFilledOption],
		[percentUniqueOption.label, percentUniqueOption],
		[earliestDateOption.label, earliestDateOption],
		[latestDateOption.label, latestDateOption],
		[dateRangeDaysOption.label, dateRangeDaysOption],
		[dateRangeMonthsOption.label, dateRangeMonthsOption],
	]),
	"Date time": new Map<string, AggregationFnOption<unknown>>([
		[emptyOption.label, emptyOption],
		[filledOption.label, filledOption],
		[uniqueOption.label, uniqueOption],
		[percentEmptyOption.label, percentEmptyOption],
		[percentFilledOption.label, percentFilledOption],
		[percentUniqueOption.label, percentUniqueOption],
		[earliestDateOption.label, earliestDateOption],
		[latestDateOption.label, latestDateOption],
		[dateRangeDaysOption.label, dateRangeDaysOption],
		[dateRangeMonthsOption.label, dateRangeMonthsOption],
	]),
	"Email": new Map<string, AggregationFnOption<unknown>>([
		[emptyOption.label, emptyOption],
		[filledOption.label, filledOption],
		[uniqueOption.label, uniqueOption],
		[percentEmptyOption.label, percentEmptyOption],
		[percentFilledOption.label, percentFilledOption],
		[percentUniqueOption.label, percentUniqueOption],
	]),
	"Text": new Map<string, AggregationFnOption<unknown>>([
		[emptyOption.label, emptyOption],
		[filledOption.label, filledOption],
		[uniqueOption.label, uniqueOption],
		[percentEmptyOption.label, percentEmptyOption],
		[percentFilledOption.label, percentFilledOption],
		[percentUniqueOption.label, percentUniqueOption],
	]),
	"Tag": new Map<string, AggregationFnOption<unknown>>([
		[emptyOption.label, emptyOption],
		[filledOption.label, filledOption],
		[uniqueOption.label, uniqueOption],
		[percentEmptyOption.label, percentEmptyOption],
		[percentFilledOption.label, percentFilledOption],
		[percentUniqueOption.label, percentUniqueOption],
	]),
	"Person": new Map<string, AggregationFnOption<unknown>>([
		[emptyOption.label, emptyOption],
		[filledOption.label, filledOption],
		[uniqueOption.label, uniqueOption],
		[percentEmptyOption.label, percentEmptyOption],
		[percentFilledOption.label, percentFilledOption],
		[percentUniqueOption.label, percentUniqueOption],
	]),
	"Auto": new Map<string, AggregationFnOption<unknown>>([
		[emptyOption.label, emptyOption],
		[filledOption.label, filledOption],
		[uniqueOption.label, uniqueOption],
		[percentEmptyOption.label, percentEmptyOption],
		[percentFilledOption.label, percentFilledOption],
		[percentUniqueOption.label, percentUniqueOption],
	]),
	"Link to record": new Map<string, AggregationFnOption<unknown>>([
		[emptyOption.label, emptyOption],
		[filledOption.label, filledOption],
		[uniqueOption.label, uniqueOption],
		[percentEmptyOption.label, percentEmptyOption],
		[percentFilledOption.label, percentFilledOption],
		[percentUniqueOption.label, percentUniqueOption],
	]),
	"Currency": new Map<string, AggregationFnOption<unknown>>([
		[sumOption.label, sumOption],
		[meanOption.label, meanOption],
		[medianOption.label, medianOption],
		[minOption.label, minOption],
		[maxOption.label, maxOption],
		[rangeOption.label, rangeOption],
		[extentOption.label, extentOption],
		[standardDeviationOption.label, standardDeviationOption],
		[emptyOption.label, emptyOption],
		[filledOption.label, filledOption],
		[uniqueOption.label, uniqueOption],
		[percentEmptyOption.label, percentEmptyOption],
		[percentFilledOption.label, percentFilledOption],
		[percentUniqueOption.label, percentUniqueOption],
	]),
	"Percent": new Map<string, AggregationFnOption<unknown>>([
		[sumOption.label, sumOption],
		[meanOption.label, meanOption],
		[medianOption.label, medianOption],
		[minOption.label, minOption],
		[maxOption.label, maxOption],
		[rangeOption.label, rangeOption],
		[extentOption.label, extentOption],
		[standardDeviationOption.label, standardDeviationOption],
		[emptyOption.label, emptyOption],
		[filledOption.label, filledOption],
		[uniqueOption.label, uniqueOption],
		[percentEmptyOption.label, percentEmptyOption],
		[percentFilledOption.label, percentFilledOption],
		[percentUniqueOption.label, percentUniqueOption],
	]),
	"Reaction": new Map<string, AggregationFnOption<unknown>>([
		[emptyOption.label, emptyOption],
		[filledOption.label, filledOption],
	]),
	"Emoji reactions": new Map<string, AggregationFnOption<unknown>>([
		[emptyOption.label, emptyOption],
		[filledOption.label, filledOption],
	]),
	"Multiline text": new Map<string, AggregationFnOption<unknown>>([
		[emptyOption.label, emptyOption],
		[filledOption.label, filledOption],
	]),
	"Table": new Map<string, AggregationFnOption<unknown>>([
		[emptyOption.label, emptyOption],
		[filledOption.label, filledOption],
	]),
	"Progress bar": new Map<string, AggregationFnOption<unknown>>([
		[sumOption.label, sumOption],
		[meanOption.label, meanOption],
		[medianOption.label, medianOption],
		[minOption.label, minOption],
		[maxOption.label, maxOption],
		[rangeOption.label, rangeOption],
		[extentOption.label, extentOption],
		[standardDeviationOption.label, standardDeviationOption],
		[emptyOption.label, emptyOption],
		[filledOption.label, filledOption],
		[uniqueOption.label, uniqueOption],
		[percentEmptyOption.label, percentEmptyOption],
		[percentFilledOption.label, percentFilledOption],
		[percentUniqueOption.label, percentUniqueOption],
	]),
};

export type BuiltInAggregationFn = keyof typeof aggregationFns;
